/**
 * @file coupon-form-utils.ts
 *
 * Pure helpers for the coupon form: deriving editable fields from a coupon, dirty
 * comparison, the validation predicates (numeric amounts, percentage ceiling,
 * non-negative limits, expiry not in the past), and building the
 * {@link ShopCouponPayload} sent on save. Keeping these pure makes the mapping and
 * validation logic unit-testable without rendering. The payload mapping mirrors
 * the Website Builder v2 coupon form (date-only expiry, integer usage limit,
 * blank optionals omitted); the api seam stamps the expiry's end-of-day ISO time.
 */

import type { ShopCoupon, ShopCouponPayload } from '../../api/shop-types';
import type { CouponFormFields, DiscountType } from './coupon-form-types';

/** Coerces an arbitrary discount-type string to a known type (default `"percent"`). */
function toDiscountType(value?: string): DiscountType {
  return value === 'fixed_cart' || value === 'fixed_product' ? value : 'percent';
}

/**
 * Derives the editable form fields from a source coupon. The expiry ISO string is
 * reduced to its `YYYY-MM-DD` date part (the native date input value), and the
 * nullable/numeric fields become strings.
 *
 * @param coupon - The source coupon, or `undefined` for create mode.
 *
 * @returns The initial {@link CouponFormFields}.
 */
export function deriveFields(coupon?: ShopCoupon): CouponFormFields {
  return {
    code: coupon?.code ?? '',
    discount_type: toDiscountType(coupon?.discount_type),
    amount: coupon?.amount ?? '',
    description: coupon?.description ?? '',
    date_expires: coupon?.date_expires ? coupon.date_expires.split('T')[0] : '',
    usage_limit: coupon?.usage_limit != null ? String(coupon.usage_limit) : '',
    minimum_amount: coupon?.minimum_amount ?? '',
    maximum_amount: coupon?.maximum_amount ?? '',
    individual_use: coupon?.individual_use ?? false,
    free_shipping: coupon?.free_shipping ?? false,
  };
}

/**
 * Shallow-compares two field sets for dirty tracking.
 *
 * @param a - One field set.
 * @param b - The other field set.
 *
 * @returns `true` when every field is strictly equal.
 */
export function shallowEqual(a: CouponFormFields, b: CouponFormFields): boolean {
  const keys = Object.keys(a) as (keyof CouponFormFields)[];
  return keys.every((key) => a[key] === b[key]);
}

/**
 * Whether a required amount value is invalid: not a finite number, or negative.
 * Emptiness is treated as "required" separately by the caller.
 *
 * @param value - The amount input.
 *
 * @returns `true` when the value is not a finite, non-negative number.
 */
export function isInvalidAmount(value: string): boolean {
  const num = Number(value);
  return !Number.isFinite(num) || num < 0;
}

/**
 * Whether a percentage discount exceeds 100%.
 *
 * @param type - The discount type.
 * @param value - The amount input.
 *
 * @returns `true` only for a percentage discount greater than 100.
 */
export function isPercentTooHigh(type: DiscountType, value: string): boolean {
  return type === 'percent' && Number(value) > 100;
}

/**
 * Whether an optional usage-limit value is invalid: present but not a
 * non-negative integer.
 *
 * @param value - The usage-limit input.
 *
 * @returns `true` when the value is present and not a whole number ≥ 0.
 */
export function isInvalidUsageLimit(value: string): boolean {
  if (value.trim() === '') {
    return false;
  }
  const num = Number(value);
  return !Number.isInteger(num) || num < 0;
}

/**
 * Whether an optional money value is invalid: present but not a finite,
 * non-negative number (used for the minimum and maximum spend fields).
 *
 * @param value - The money input.
 *
 * @returns `true` when the value is present and not a non-negative number.
 */
export function isInvalidOptionalAmount(value: string): boolean {
  if (value.trim() === '') {
    return false;
  }
  const num = Number(value);
  return !Number.isFinite(num) || num < 0;
}

/** Formats a date as a local `YYYY-MM-DD` string (timezone-safe for comparison). */
function toDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Whether an expiry date (the `YYYY-MM-DD` input value) is before today. An empty
 * value is allowed (the coupon never expires); today itself is allowed.
 *
 * @param value - The date input value.
 * @param now - The reference time (defaults to now; injectable for tests).
 *
 * @returns `true` when the date is strictly before today.
 */
export function isPastDate(value: string, now: Date = new Date()): boolean {
  if (value.trim() === '') {
    return false;
  }
  return value < toDateString(now);
}

/**
 * Builds the write payload from the form's fields. `code`, `discount_type`,
 * `amount`, and the two toggles are always sent; the optional limits, expiry, and
 * description are omitted when blank. The expiry is the date-only `YYYY-MM-DD` value
 * (the api seam stamps the end-of-day time and the ISO timezone) and the usage limit
 * is parsed to an integer.
 *
 * @param fields - The current form fields.
 *
 * @returns The {@link ShopCouponPayload} to POST/PUT.
 */
export function buildCouponPayload(fields: CouponFormFields): ShopCouponPayload {
  const payload: ShopCouponPayload = {
    code: fields.code.trim(),
    discount_type: fields.discount_type,
    amount: fields.amount.trim(),
    individual_use: fields.individual_use,
    free_shipping: fields.free_shipping,
  };
  if (fields.date_expires) {
    payload.date_expires = fields.date_expires;
  }
  const usageLimit = fields.usage_limit.trim();
  if (usageLimit) {
    payload.usage_limit = Number.parseInt(usageLimit, 10);
  }
  const minimum = fields.minimum_amount.trim();
  if (minimum) {
    payload.minimum_amount = minimum;
  }
  const maximum = fields.maximum_amount.trim();
  if (maximum) {
    payload.maximum_amount = maximum;
  }
  const description = fields.description.trim();
  if (description) {
    payload.description = description;
  }
  return payload;
}
